import "./GroupCardShared.scss";

import clsx from "clsx";
import type { FC, ReactNode } from "react";

import { userActivityIcons } from "components/materials/section/helpers";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ListItem } from "mds/components/ListItem";
import { Tooltip } from "mds/components/Tooltip";
import { CircleInfoIcon, PersonSingleIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { determineActivityStatus, selectActivityStatus } from "store/selectors";
import { sortByFirstName } from "store/selectors/sorting";
import type { ExtendedPageGroupUserType, NestedPageGroupType } from "store/selectors/types";
import { getUserFullName } from "utils/user-utils";

interface PageGroupInfoCardProps {
  className?: string;
  pageGroup?: NestedPageGroupType;
  pageGroupIndex: number;
  children?: ReactNode;
  isActive: boolean;
  shouldFilterForReadiedUsers?: boolean;
  isInstructorView?: boolean;
}

export const PageGroupInfoCard: FC<PageGroupInfoCardProps> = ({
  className,
  pageGroup,
  pageGroupIndex,
  children,
  shouldFilterForReadiedUsers = false,
  isActive,
  isInstructorView = false,
}) => {
  const activityStatus = useAppSelector(selectActivityStatus);

  if (!pageGroup) return null;

  const pageGroupUsers = shouldFilterForReadiedUsers
    ? pageGroup.page_group_users.filter((pageGroupUser) => pageGroupUser.is_ready)
    : pageGroup.page_group_users;

  const sortedPageGroupUsers = pageGroupUsers.toSorted(sortByFirstName);

  const getUserStatus = (pageGroupUser: ExtendedPageGroupUserType) => {
    return determineActivityStatus(activityStatus[pageGroupUser.user_id]);
  };

  return (
    <Card className={clsx("group-card", className)} isActive={isActive}>
      <div className="flex w-full items-center justify-between">
        <div className="h3 ml-2 flex items-center gap-1">
          {t("common.group_title", {
            number: pageGroupIndex + 1,
          })}
          {pageGroup.auto_created && (
            <Tooltip
              content={t(
                isInstructorView
                  ? "tooltip.auto_created_group"
                  : "tooltip.auto_created_group_student",
              )}
            >
              <CircleInfoIcon className="text-orange" size="s" />
            </Tooltip>
          )}
        </div>
        <div className="flex">
          <div className="h6 flex items-center justify-center gap-1 px-2 text-black-tint-40">
            <PersonSingleIcon />
            {sortedPageGroupUsers.length}
          </div>
          {children}
        </div>
      </div>
      <div className="flex flex-wrap">
        {sortedPageGroupUsers.map((pageGroupUser) => (
          <ListItem className="gap-1" key={pageGroupUser.id}>
            {userActivityIcons[getUserStatus(pageGroupUser)]}{" "}
            <div className="min-w-12 truncate">{getUserFullName(pageGroupUser)}</div>
          </ListItem>
        ))}
        {sortedPageGroupUsers.length === 0 && (
          <div className="body-s ml-2 pt-2 text-black-tint-55">
            {t("page_group_info_card.no_one_in_group")}
          </div>
        )}
      </div>
    </Card>
  );
};
